import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Hand, Flame, Volume2, VolumeX, Sparkles, Wind } from "lucide-react";
import { useTranslation } from "react-i18next";
import { mix, alpha } from "@/lib/color";

/**
 * BlowCandles — "Blow the candles to reveal your gift".
 *
 * Two inputs feed ONE "wind" value (0..1) that the flames react to live
 * (they bend, shrink and flicker harder as the wind rises):
 *   • mic  → low-frequency spectral energy (20-250 Hz, where a real blow
 *            sits; voice lives at 300-3000 Hz). A 1.5 s ambient calibration
 *            sets the threshold to max(minAbs, ambient × mul).
 *   • tap  → a breath gauge: every tap adds 1/tapsPerCandle, holding the
 *            finger fills it continuously, and it slowly decays when idle.
 *            Full gauge = one candle out.
 *
 * Data-flow mirrors SpinWheel: parent owns `spinning` / `resultIndex` /
 * `onStart` / `onRevealed`; we only run the in-game cinematic.
 */
// Each difficulty: multiplier applied on top of the calibrated ambient noise,
// an absolute floor (linear amplitude in the 20-250 Hz band; 0.012 ≈ -38 dB)
// and the number of taps needed per candle in tap mode.
const DIFFICULTY = {
  easy: { ambientMul: 3, minAbs: 0.007, tapsPerCandle: 4 },
  normal: { ambientMul: 4, minAbs: 0.012, tapsPerCandle: 6 },
  hard: { ambientMul: 6, minAbs: 0.022, tapsPerCandle: 9 },
};

const BLOW_FREQ_MIN_HZ = 20;
const BLOW_FREQ_MAX_HZ = 250;
const CALIBRATION_MS = 1500;
const GAUGE_DECAY_PER_S = 0.25;
const SNUFF_LOCK_MS = 380;
const HOLD_GUST_EVERY_MS = 170;
// Mic → gauge: fill speed per (ratio-to-threshold − 0.5) per second. At exactly
// the threshold a candle goes out in ~0.6 s; a weaker steady blow still works.
const MIC_FILL_RATE = 3.3;
const MIC_ACTIVE_RATIO = 0.5;

const CANDLE_COLORS = ["#fbbf24", "#f472b6", "#60a5fa", "#34d399", "#c084fc", "#fb7185", "#fde68a", "#2dd4bf"];

export default function BlowCandles({
  segments,
  winLabel,
  isWin,
  spinning,
  resultIndex,
  onStart,
  onRevealed,
  candlesCount = 5,
  difficulty = "normal",
  cakeStyle = "classic",
  maxDuration = 20,
  themeColor = "#A855F7",
  merchantLogo,
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(null); // null | "mic" | "tap"
  const [calibrating, setCalibrating] = useState(false);
  const [calibrationProgress, setCalibrationProgress] = useState(0);
  const [snuffed, setSnuffed] = useState(0);
  const [timeLeft, setTimeLeft] = useState(maxDuration);
  const [micLevel, setMicLevel] = useState(0);
  const [micError, setMicError] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [revealDone, setRevealDone] = useState(false);
  const [timeout, setTimeoutState] = useState(false);
  const [gusts, setGusts] = useState([]);

  const count = Math.max(1, Math.min(10, Number(candlesCount) || 5));
  const diff = DIFFICULTY[difficulty] || DIFFICULTY.normal;

  const audioCtxRef = useRef(null);
  const sfxCtxRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const gameStartedAtRef = useRef(null);
  const timerRef = useRef(null);
  const thresholdRef = useRef(diff.minAbs);
  const binRangeRef = useRef({ min: 1, max: 12 });
  // `onStart` (= session POST) must fire exactly once even if "all snuffed"
  // and "timeout" become true in the same render frame.
  const submittedRef = useRef(false);

  // Wind / breath gauge — refs + direct DOM writes so the 60 fps loop never
  // re-renders React.
  const stageRef = useRef(null);
  const gaugeBarRef = useRef(null);
  const gaugeRef = useRef(0);
  const holdingRef = useRef(false);
  const lockUntilRef = useRef(0);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const lastGustAtRef = useRef(0);
  const micRatioRef = useRef(0);

  const setWind = useCallback((v) => {
    const w = Math.max(0, Math.min(1, v || 0));
    if (stageRef.current) stageRef.current.style.setProperty("--wind", w.toFixed(3));
    if (gaugeBarRef.current) gaugeBarRef.current.style.width = `${Math.round(w * 100)}%`;
  }, []);

  const safeCallOnStart = useCallback(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    try { onStart?.(); } catch (e) { /* ignore */ }
  }, [onStart]);

  const cleanupMic = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) { /* ignore */ }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    micRatioRef.current = 0;
  }, []);

  useEffect(() => () => cleanupMic(), [cleanupMic]);

  useEffect(() => {
    if (!mode || revealing || calibrating) return;
    gameStartedAtRef.current = Date.now();
    setTimeLeft(maxDuration);
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - gameStartedAtRef.current) / 1000;
      setTimeLeft(Math.max(0, maxDuration - elapsed));
      if (Date.now() - gameStartedAtRef.current >= maxDuration * 1000) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 100);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [mode, maxDuration, revealing, calibrating]);

  /* ---------------- sound FX (WebAudio, no assets) ---------------- */
  const sfxCtx = useCallback(() => {
    if (!sfxCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      sfxCtxRef.current = new Ctx();
    }
    if (sfxCtxRef.current.state === "suspended") sfxCtxRef.current.resume().catch(() => {});
    return sfxCtxRef.current;
  }, []);

  const playPuffSound = useCallback(() => {
    if (!soundOn) return;
    try {
      const ctx = sfxCtx();
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.25, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2.2);
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(900, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.22);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.5, ctx.currentTime);
      src.connect(filter).connect(g).connect(ctx.destination);
      src.start();
    } catch (e) { /* ignore */ }
  }, [soundOn, sfxCtx]);

  const playChime = useCallback(() => {
    if (!soundOn) return;
    try {
      const ctx = sfxCtx();
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        const t0 = ctx.currentTime + i * 0.09;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.16, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.7);
        o.connect(g).connect(ctx.destination);
        o.start(t0);
        o.stop(t0 + 0.75);
      });
    } catch (e) { /* ignore */ }
  }, [soundOn, sfxCtx]);

  const handleSnuff = useCallback(() => {
    setSnuffed((s) => Math.min(count, s + 1));
    if (navigator.vibrate) navigator.vibrate(30);
    playPuffSound();
  }, [count, playPuffSound]);

  /* ---------------- end-of-game flow (unchanged) ---------------- */
  useEffect(() => {
    if (revealing) return;
    if (snuffed >= count) {
      setRevealing(true);
      if (timerRef.current) clearInterval(timerRef.current);
      cleanupMic();
      setWind(0);
      safeCallOnStart();
    }
  }, [snuffed, count, cleanupMic, safeCallOnStart, revealing, setWind]);

  useEffect(() => {
    if (!revealing || revealDone) return;
    if (spinning) return;
    if (resultIndex == null) return;
    playChime();
    const id = setTimeout(() => {
      setRevealDone(true);
      onRevealed?.();
    }, 1800);
    return () => clearTimeout(id);
  }, [revealing, resultIndex, spinning, revealDone, onRevealed, playChime]);

  useEffect(() => {
    if (!mode || revealing) return;
    if (timeLeft <= 0 && snuffed < count) {
      setTimeoutState(true);
      cleanupMic();
      setWind(0);
      safeCallOnStart();
    }
  }, [timeLeft, mode, revealing, snuffed, count, cleanupMic, safeCallOnStart, setWind]);

  useEffect(() => {
    if (!timeout) return;
    if (spinning) return;
    if (resultIndex == null) return;
    const id = setTimeout(() => { onRevealed?.(); }, 900);
    return () => clearTimeout(id);
  }, [timeout, spinning, resultIndex, onRevealed]);

  /* ---------------- microphone ---------------- */
  async function startMic() {
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      streamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      if (ctx.state === "suspended") {
        try { await ctx.resume(); } catch (e) { /* ignore */ }
      }
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 4096;
      analyser.smoothingTimeConstant = 0.4;
      source.connect(analyser);
      analyserRef.current = analyser;
      const hzPerBin = ctx.sampleRate / analyser.fftSize;
      binRangeRef.current = {
        min: Math.max(1, Math.floor(BLOW_FREQ_MIN_HZ / hzPerBin)),
        max: Math.ceil(BLOW_FREQ_MAX_HZ / hzPerBin),
      };
      thresholdRef.current = diff.minAbs;
      setMode("mic");
      runCalibration();
    } catch (e) {
      setMicError(t("blow.mic_denied"));
    }
  }

  function runCalibration() {
    setCalibrating(true);
    setCalibrationProgress(0);
    const samples = [];
    const t0 = Date.now();
    const sample = () => {
      const elapsed = Date.now() - t0;
      setCalibrationProgress(Math.min(1, elapsed / CALIBRATION_MS));
      const e = readLowFreqEnergy();
      setMicLevel(e);
      samples.push(e);
      if (elapsed >= CALIBRATION_MS) {
        samples.sort((a, b) => a - b);
        const p90 = samples[Math.floor(samples.length * 0.9)] || 0;
        thresholdRef.current = Math.max(diff.minAbs, p90 * diff.ambientMul);
        setCalibrating(false);
        loopMic();
        return;
      }
      rafRef.current = requestAnimationFrame(sample);
    };
    sample();
  }

  function readLowFreqEnergy() {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    const { min, max } = binRangeRef.current;
    const buf = new Float32Array(analyser.frequencyBinCount);
    analyser.getFloatFrequencyData(buf);
    let sum = 0;
    let n = 0;
    for (let i = min; i <= max && i < buf.length; i++) {
      const db = Math.max(-100, buf[i]);
      sum += Math.pow(10, db / 20);
      n++;
    }
    return n > 0 ? sum / n : 0;
  }

  function loopMic() {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const tick = () => {
      // Smoothed energy-to-threshold ratio; the shared gauge loop turns it
      // into candle snuffs (tolerant to frame-to-frame dips in the signal).
      const ratio = readLowFreqEnergy() / thresholdRef.current;
      micRatioRef.current = micRatioRef.current * 0.55 + ratio * 0.45;
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }

  /* ---------------- tap / hold breath gauge ---------------- */
  const spawnGust = useCallback((x, y) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const parts = Array.from({ length: 7 }, () => ({
      dx: -34 + Math.random() * 68,
      dy: -(80 + Math.random() * 110),
      rot: -28 + Math.random() * 56,
      len: 12 + Math.random() * 18,
      delay: Math.random() * 0.07,
    }));
    setGusts((g) => [...g.slice(-5), { id, x, y, parts }]);
    setTimeout(() => setGusts((g) => g.filter((p) => p.id !== id)), 700);
  }, []);

  useEffect(() => {
    if (!mode || revealing || timeout || calibrating) return;
    let last = performance.now();
    const fillPerS = 1 / (0.22 * diff.tapsPerCandle);
    let raf;
    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      let g = gaugeRef.current;
      // Full gauge → one candle out (checked BEFORE decay so a burst of taps
      // that lands exactly on 1 still counts).
      if (g >= 0.999 && now >= lockUntilRef.current) {
        lockUntilRef.current = now + SNUFF_LOCK_MS;
        g = 0.15;
        handleSnuff();
      }
      const micRatio = mode === "mic" ? micRatioRef.current : 0;
      const blowing = micRatio > MIC_ACTIVE_RATIO;
      if (holdingRef.current) g += dt * fillPerS;
      if (blowing) g += dt * (micRatio - MIC_ACTIVE_RATIO) * MIC_FILL_RATE;
      if (!holdingRef.current && !blowing) g -= dt * GAUGE_DECAY_PER_S;
      g = Math.max(0, Math.min(1, g));
      if (holdingRef.current && now - lastGustAtRef.current > HOLD_GUST_EVERY_MS) {
        lastGustAtRef.current = now;
        spawnGust(lastPointerRef.current.x, lastPointerRef.current.y);
      }
      gaugeRef.current = g;
      // Flames react to the live breath even before the gauge fills.
      setWind(Math.max(g, Math.min(1, micRatio * 0.7)));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, revealing, timeout, calibrating, diff.tapsPerCandle, handleSnuff, setWind, spawnGust]);

  function onCakePointerDown(e) {
    if (!mode || revealing || timeout || calibrating) return;
    const rect = e.currentTarget.getBoundingClientRect();
    lastPointerRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    lastGustAtRef.current = performance.now();
    holdingRef.current = true;
    gaugeRef.current = Math.min(1, gaugeRef.current + 1 / diff.tapsPerCandle);
    spawnGust(lastPointerRef.current.x, lastPointerRef.current.y);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
  }
  function onCakePointerMove(e) {
    if (!holdingRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    lastPointerRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }
  function onCakePointerUp() {
    holdingRef.current = false;
  }

  const lit = Math.max(0, count - snuffed);
  const spec = CAKE_SPECS[cakeStyle] || CAKE_SPECS.classic;
  const slots = useMemo(() => candleSlots(spec.top, count), [spec, count]);
  const shakeX = snuffed % 2 ? [0, -4, 4, -2, 2, 0] : [0, 4, -4, 2, -2, 0];

  return (
    <div className="relative mx-auto w-full max-w-md">
      {!mode && (
        <ModePicker
          onMic={startMic}
          onTap={() => setMode("tap")}
          micError={micError}
          themeColor={themeColor}
        />
      )}

      {mode && (
        <motion.div
          ref={stageRef}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[32px] border border-white/10 p-4 backdrop-blur-2xl sm:p-5"
          style={{
            background: `linear-gradient(180deg, ${alpha(themeColor, 0.34)} 0%, rgba(10,7,18,0.86) 55%, rgba(5,4,10,0.94) 100%)`,
            boxShadow: `0 40px 100px -40px ${alpha(themeColor, 0.6)}, inset 0 1px 0 rgba(255,255,255,0.08)`,
          }}
          data-testid="blow-candles-stage"
        >
          <AmberEmbers />

          {/* HUD */}
          <div className="relative z-10 mb-2 flex items-center justify-between">
            <div
              className="flex items-center gap-2 rounded-full border border-white/10 bg-black/25 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white/85"
              data-testid="blow-candles-hud-count"
            >
              <Flame className="h-3.5 w-3.5 text-amber-300" />
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={lit}
                  initial={{ y: -8, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 8, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="inline-block tabular-nums"
                >
                  {lit}
                </motion.span>
              </AnimatePresence>
              <span className="text-white/30">/</span>
              <span className="tabular-nums">{count}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSoundOn((s) => !s)}
                className="rounded-full border border-white/10 bg-black/25 p-2 text-white/70 transition hover:bg-white/10 hover:text-white"
                data-testid="blow-candles-sound-toggle"
                aria-label="toggle sound"
              >
                {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
              </button>
              <TimerRing value={timeLeft} max={maxDuration} themeColor={themeColor} />
            </div>
          </div>

          {/* Scene */}
          <motion.div
            className="relative mx-auto aspect-[6/7] w-full max-w-[380px] select-none touch-none"
            style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
            onPointerDown={onCakePointerDown}
            onPointerMove={onCakePointerMove}
            onPointerUp={onCakePointerUp}
            onPointerCancel={onCakePointerUp}
            onContextMenu={(e) => e.preventDefault()}
            data-testid="blow-candles-cake"
            animate={snuffed ? { x: shakeX } : { x: 0 }}
            transition={{ duration: 0.35 }}
          >
            {/* Warm candle light on the scene — dims as candles go out */}
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-[70%]"
              animate={{ opacity: revealing || timeout ? 0 : 0.25 + 0.6 * (lit / count) }}
              transition={{ duration: 0.6 }}
              style={{
                background:
                  "radial-gradient(ellipse at 50% 55%, rgba(255,196,110,0.75) 0%, rgba(251,146,60,0.28) 35%, transparent 70%)",
                filter: "blur(14px)",
              }}
            />

            <CakeScene
              styleKey={cakeStyle}
              themeColor={themeColor}
              slots={slots}
              snuffed={snuffed}
              merchantLogo={merchantLogo}
            />

            {/* Gust particles (tap / hold feedback) */}
            {gusts.map((g) => (
              <React.Fragment key={g.id}>
                <motion.span
                  className="pointer-events-none absolute rounded-full border border-white/70"
                  style={{ left: g.x - 14, top: g.y - 14, width: 28, height: 28 }}
                  initial={{ scale: 0.4, opacity: 0.8 }}
                  animate={{ scale: 2.2, opacity: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                />
                {g.parts.map((p, i) => (
                  <motion.span
                    key={i}
                    className="pointer-events-none absolute rounded-full"
                    style={{
                      left: g.x,
                      top: g.y,
                      width: 3,
                      height: p.len,
                      rotate: p.rot,
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 100%)",
                    }}
                    initial={{ x: 0, y: 0, opacity: 0.95, scaleY: 0.4 }}
                    animate={{ x: p.dx, y: p.dy, opacity: 0, scaleY: 1.1 }}
                    transition={{ duration: 0.55, ease: "easeOut", delay: p.delay }}
                  />
                ))}
              </React.Fragment>
            ))}

            {/* Reveal burst */}
            <AnimatePresence>
              {revealing && !timeout && (
                <>
                  <motion.div
                    key="reveal-halo"
                    aria-hidden
                    initial={{ opacity: 0, scale: 0.3 }}
                    animate={{ opacity: 1, scale: 2.2 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="pointer-events-none absolute inset-0 rounded-[32px]"
                    style={{
                      background: `radial-gradient(closest-side, rgba(253,224,71,0.9), ${alpha(themeColor, 0.45)}, transparent 75%)`,
                      filter: "blur(2px)",
                    }}
                  />
                  <ConfettiBurst themeColor={themeColor} />
                </>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Breath gauge + hints */}
          {mode === "mic" && calibrating ? (
            <CalibrationOverlay progress={calibrationProgress} level={micLevel} themeColor={themeColor} />
          ) : (
            <div className="relative z-10 mt-2">
              <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.26em] text-white/55">
                <span className="inline-flex items-center gap-1.5">
                  <Wind className="h-3 w-3" />
                  {t("blow.gauge_label")}
                </span>
                <span className="inline-flex items-center gap-1.5 normal-case tracking-normal text-white/60">
                  {revealing && !timeout ? (
                    <span className="inline-flex items-center gap-1 font-bold text-amber-200">
                      <Sparkles className="h-3 w-3" />
                      {t("blow.all_out")}
                    </span>
                  ) : timeout ? null : mode === "mic" ? (
                    <><Mic className="h-3 w-3" />{t("blow.mic_hint")}</>
                  ) : (
                    <><Hand className="h-3 w-3" />{t("blow.tap_hint")}</>
                  )}
                </span>
              </div>
              <div className="relative h-2.5 overflow-hidden rounded-full bg-white/10" data-testid="blow-candles-gauge">
                <div
                  ref={gaugeBarRef}
                  className="h-full rounded-full"
                  style={{
                    width: "0%",
                    background: `linear-gradient(90deg, ${alpha(themeColor, 0.95)} 0%, #fbbf24 60%, #fff4c2 100%)`,
                    boxShadow: "0 0 14px rgba(251,191,36,0.55)",
                    transition: "width 60ms linear",
                  }}
                />
                {mode === "mic" && (
                  <div
                    className="pointer-events-none absolute -top-0.5 h-[calc(100%+4px)] w-[2px] rounded bg-white/80"
                    style={{ left: "70%" }}
                  />
                )}
              </div>
            </div>
          )}

          {revealing && !timeout && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative z-10 mt-3 text-center"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-400/10 px-4 py-1.5 text-sm font-bold text-amber-100">
                <Sparkles className="h-3.5 w-3.5" />
                {t("blow.revealing")}
              </div>
            </motion.div>
          )}
          {timeout && (
            <div className="relative z-10 mt-3 text-center text-sm font-semibold text-rose-200">
              {t("blow.timeout")}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Cake scene                                                          */
/* ================================================================== */

// Where candles stand for each style: the top-surface ellipse (SVG units,
// viewBox 0 0 360 420).
const CAKE_SPECS = {
  classic: { top: { cx: 180, cy: 214, rx: 118, ry: 27 } },
  cupcake: { top: { cx: 180, cy: 214, rx: 70, ry: 16 } },
  macaron: { top: { cx: 180, cy: 222, rx: 86, ry: 14 } },
  tiered: { top: { cx: 180, cy: 160, rx: 58, ry: 13 } },
};

// Spread candles over the top surface: one shallow front arc up to 5, then a
// front + back row. `order` = snuff order (front row left→right, then back).
function candleSlots(top, count) {
  const rows = count > 5 ? [Math.ceil(count / 2), Math.floor(count / 2)] : [count];
  const out = [];
  rows.forEach((n, r) => {
    const front = r === 0;
    const spread = top.rx * (front ? 0.8 : 0.58);
    const crowd = Math.max(0.68, Math.min(1, (2 * spread + 26) / (n * 27)));
    const baseY = top.cy + top.ry * (front ? 0.42 : -0.42);
    for (let i = 0; i < n; i++) {
      const u = n === 1 ? 0 : (i / (n - 1)) * 2 - 1;
      out.push({
        order: out.length,
        x: top.cx + u * spread,
        y: baseY + top.ry * 0.32 * (1 - u * u),
        s: crowd * (front ? 1 : 0.86),
      });
    }
  });
  return out.sort((a, b) => a.y - b.y);
}

function CakeScene({ styleKey, themeColor, slots, snuffed, merchantLogo }) {
  const frosting = mix(themeColor, 0.78);
  const frostingDeep = mix(themeColor, 0.55);
  return (
    <svg
      viewBox="0 0 360 420"
      className="relative h-full w-full"
      preserveAspectRatio="xMidYMax meet"
      aria-hidden
    >
      <defs>
        <radialGradient id="bc-floor" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(0,0,0,0.6)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
        <linearGradient id="bc-side" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor={mix(themeColor, 0.26)} />
          <stop offset="0.42" stopColor={themeColor} />
          <stop offset="1" stopColor={mix(themeColor, -0.42)} />
        </linearGradient>
        <linearGradient id="bc-side-alt" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor={mix(themeColor, 0.62)} />
          <stop offset="0.42" stopColor={mix(themeColor, 0.42)} />
          <stop offset="1" stopColor={mix(themeColor, -0.08)} />
        </linearGradient>
        <linearGradient id="bc-top" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={mix(themeColor, 0.9)} />
          <stop offset="1" stopColor={frostingDeep} />
        </linearGradient>
        <linearGradient id="bc-plate" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fffdf8" />
          <stop offset="1" stopColor="#d9d1c3" />
        </linearGradient>
        <linearGradient id="bc-plate-side" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#e8e0d2" />
          <stop offset="1" stopColor="#a89c8a" />
        </linearGradient>
        <radialGradient id="bc-flame-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255,214,120,0.85)" />
          <stop offset="50%" stopColor="rgba(255,160,60,0.28)" />
          <stop offset="100%" stopColor="rgba(255,160,60,0)" />
        </radialGradient>
        <linearGradient id="bc-flame" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ff7a18" />
          <stop offset="0.55" stopColor="#ffb340" />
          <stop offset="1" stopColor="#ffd98a" />
        </linearGradient>
        {CANDLE_COLORS.map((c, i) => (
          <linearGradient key={i} id={`bc-candle-${i}`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor={mix(c, 0.45)} />
            <stop offset="0.5" stopColor={c} />
            <stop offset="1" stopColor={mix(c, -0.35)} />
          </linearGradient>
        ))}
      </defs>

      <CakeStand />

      {styleKey === "cupcake" && <Cupcake theme={themeColor} frosting={frosting} />}
      {styleKey === "macaron" && <Macaron theme={themeColor} frosting={frosting} />}
      {styleKey === "tiered" && <Tiered theme={themeColor} frosting={frosting} />}
      {(!styleKey || styleKey === "classic") && <Classic theme={themeColor} frosting={frosting} />}

      {slots.map((slot) => (
        <Candle
          key={slot.order}
          x={slot.x}
          y={slot.y}
          s={slot.s}
          colorIndex={slot.order % CANDLE_COLORS.length}
          snuffed={slot.order < snuffed}
          phase={(slot.order * 0.17) % 0.9}
        />
      ))}
    </svg>
  );
}

function CakeStand() {
  return (
    <g>
      <ellipse cx="180" cy="408" rx="160" ry="10" fill="url(#bc-floor)" />
      {/* foot */}
      <ellipse cx="180" cy="404" rx="64" ry="9" fill="url(#bc-plate-side)" />
      <ellipse cx="180" cy="401" rx="60" ry="7" fill="#efe8dc" />
      {/* stem */}
      <path d="M170 372 h20 v30 h-20 z" fill="url(#bc-plate-side)" />
      <path d="M173 372 h5 v30 h-5 z" fill="rgba(255,255,255,0.45)" />
      {/* plate */}
      <ellipse cx="180" cy="368" rx="164" ry="24" fill="url(#bc-plate-side)" />
      <ellipse cx="180" cy="364" rx="164" ry="22" fill="url(#bc-plate)" />
      <ellipse cx="180" cy="364" rx="150" ry="17" fill="none" stroke="#d4af37" strokeWidth="1.6" opacity="0.9" />
      <ellipse cx="180" cy="364" rx="128" ry="12" fill="rgba(0,0,0,0.06)" />
    </g>
  );
}

/* One frosted cylinder: side, drips, top surface, piped borders. */
function Tier({ cx, topY, rx, ry, h, sideFill, frosting, dripSeed = 0, sprinkles = true, layers = 0 }) {
  const x0 = cx - rx;
  const w = rx * 2;
  const bottomY = topY + h;
  const drips = useMemo(() => {
    const n = Math.max(5, Math.round(w / 26));
    const step = w / n;
    let d = `M${x0 + 4} ${topY + ry + 2}`;
    for (let i = 0; i < n; i++) {
      const len = 14 + ((i * 7 + dripSeed * 5) % 4) * 6;
      d += ` q${step / 2} ${len} ${step} 0`;
    }
    return d;
  }, [w, x0, topY, ry, dripSeed]);
  const border = useMemo(() => {
    const n = Math.max(6, Math.round(w / 18));
    return Array.from({ length: n + 1 }, (_, i) => {
      const th = Math.PI * (i / n); // 0..π → front arc
      return { x: cx + rx * Math.cos(th), y: bottomY + ry * Math.sin(th) * 0.92 };
    });
  }, [w, cx, rx, ry, bottomY]);
  const dots = useMemo(() => {
    if (!sprinkles) return [];
    const seed = dripSeed * 13 + 7;
    return Array.from({ length: 12 }, (_, i) => {
      const a = ((i * 137.5 + seed * 31) % 360) * (Math.PI / 180);
      const r = 0.25 + ((i * 53 + seed) % 60) / 100;
      return {
        x: cx + rx * r * Math.cos(a),
        y: topY + ry * r * Math.sin(a),
        rot: (i * 61 + seed) % 180,
        c: CANDLE_COLORS[(i + seed) % CANDLE_COLORS.length],
      };
    });
  }, [sprinkles, cx, rx, ry, topY, dripSeed]);

  return (
    <g>
      {/* side */}
      <path d={`M${x0} ${topY} v${h} a${rx} ${ry} 0 0 0 ${w} 0 v-${h} z`} fill={sideFill} />
      {/* sponge layers peeking through */}
      {Array.from({ length: layers }, (_, i) => {
        const ly = topY + (h * (i + 1)) / (layers + 1);
        return (
          <g key={i}>
            <path d={`M${x0} ${ly} a${rx} ${ry} 0 0 0 ${w} 0`} stroke="rgba(0,0,0,0.22)" strokeWidth="5" fill="none" />
            <path d={`M${x0} ${ly - 1} a${rx} ${ry} 0 0 0 ${w} 0`} stroke="rgba(255,245,220,0.55)" strokeWidth="2" fill="none" />
          </g>
        );
      })}
      {/* vertical sheen + right shadow */}
      <path d={`M${x0 + 10} ${topY + 6} v${h - 10}`} stroke="rgba(255,255,255,0.22)" strokeWidth="8" strokeLinecap="round" />
      <path d={`M${cx + rx - 12} ${topY + 6} v${h - 10}`} stroke="rgba(0,0,0,0.18)" strokeWidth="10" strokeLinecap="round" />
      {/* bottom piped border */}
      {border.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y - 3} r="5" fill={frosting} />
      ))}
      {/* drips */}
      <path d={drips} stroke={frosting} strokeWidth="11" fill="none" strokeLinecap="round" />
      <path d={drips} stroke="rgba(255,255,255,0.35)" strokeWidth="3" fill="none" strokeLinecap="round" transform="translate(0 -3)" />
      {/* top surface */}
      <ellipse cx={cx} cy={topY} rx={rx} ry={ry} fill="url(#bc-top)" />
      <ellipse cx={cx} cy={topY - 2} rx={rx * 0.86} ry={ry * 0.72} fill="rgba(255,255,255,0.22)" />
      <ellipse cx={cx} cy={topY} rx={rx} ry={ry} fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="1" />
      {dots.map((d, i) => (
        <rect key={i} x={d.x - 4} y={d.y - 1.4} width="8" height="2.8" rx="1.4" fill={d.c} transform={`rotate(${d.rot} ${d.x} ${d.y})`} />
      ))}
    </g>
  );
}

function Classic({ frosting }) {
  return (
    <Tier cx={180} topY={214} rx={118} ry={27} h={130} sideFill="url(#bc-side)" frosting={frosting} layers={2} dripSeed={1} />
  );
}

function Tiered({ frosting }) {
  return (
    <g>
      <Tier cx={180} topY={286} rx={128} ry={28} h={60} sideFill="url(#bc-side)" frosting={frosting} dripSeed={2} sprinkles={false} />
      <Tier cx={180} topY={222} rx={92} ry={20} h={64} sideFill="url(#bc-side-alt)" frosting={frosting} dripSeed={3} sprinkles={false} />
      <Tier cx={180} topY={160} rx={58} ry={13} h={62} sideFill="url(#bc-side)" frosting={frosting} dripSeed={4} />
    </g>
  );
}

function Cupcake({ theme, frosting }) {
  const pleats = [-60, -36, -12, 12, 36, 60];
  return (
    <g>
      {/* wrapper */}
      <path d="M96 262 L264 262 L246 356 L114 356 Z" fill={mix(theme, -0.3)} />
      {pleats.map((dx, i) => (
        <path key={i} d={`M${180 + dx} 262 L${180 + dx * 0.78} 356`} stroke="rgba(0,0,0,0.28)" strokeWidth="3" />
      ))}
      <path d="M96 262 L264 262 L246 356 L114 356 Z" fill="url(#bc-side)" opacity="0.35" />
      <ellipse cx="180" cy="356" rx="66" ry="8" fill={mix(theme, -0.45)} />
      <ellipse cx="180" cy="262" rx="84" ry="14" fill={mix(theme, -0.15)} />
      {/* frosting swirl */}
      {[
        { cy: 262, rx: 92, ry: 26 },
        { cy: 244, rx: 80, ry: 22 },
        { cy: 228, rx: 66, ry: 19 },
      ].map((b, i) => (
        <g key={i}>
          <ellipse cx="180" cy={b.cy + 6} rx={b.rx} ry={b.ry} fill={mix(theme, 0.4)} />
          <ellipse cx="180" cy={b.cy} rx={b.rx} ry={b.ry} fill={frosting} />
          <ellipse cx="172" cy={b.cy - 6} rx={b.rx * 0.6} ry={b.ry * 0.45} fill="rgba(255,255,255,0.35)" />
        </g>
      ))}
      <ellipse cx="180" cy="214" rx="70" ry="16" fill="url(#bc-top)" />
      <ellipse cx="176" cy="211" rx="48" ry="9" fill="rgba(255,255,255,0.3)" />
      {[[130, 250], [156, 262], [205, 258], [232, 246], [166, 236], [200, 232]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.6" fill={CANDLE_COLORS[i % CANDLE_COLORS.length]} />
      ))}
    </g>
  );
}

function Macaron({ theme, frosting }) {
  return (
    <g>
      {/* bottom shell (dome down) */}
      <ellipse cx="180" cy="338" rx="100" ry="10" fill="rgba(0,0,0,0.25)" />
      <path d="M72 292 a108 44 0 0 0 216 0 z" fill="url(#bc-side)" />
      <path d="M72 292 a108 44 0 0 0 216 0 z" fill="rgba(0,0,0,0.22)" />
      {/* filling */}
      <path d="M74 262 h212 v30 a106 12 0 0 1 -212 0 z" fill={frosting} />
      <path d="M74 262 a106 12 0 0 0 212 0" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="3" />
      {/* foot (ruffle) — lighter so the shell reads as a dome */}
      <path d="M72 240 h216 v22 a108 12 0 0 1 -216 0 z" fill={mix(theme, 0.38)} />
      {Array.from({ length: 17 }, (_, i) => (
        <circle key={i} cx={76 + i * 13} cy={256 + (i % 2) * 3} r="2.8" fill={mix(theme, -0.2)} opacity="0.55" />
      ))}
      {/* top shell (dome) */}
      <path d="M72 240 a108 46 0 0 1 216 0 z" fill="url(#bc-side)" />
      <path d="M72 240 a108 46 0 0 1 216 0 z" fill="none" stroke="rgba(0,0,0,0.14)" strokeWidth="1.5" />
      <path d="M92 236 a88 34 0 0 1 176 0" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
      <ellipse cx="152" cy="212" rx="50" ry="9" fill="rgba(255,255,255,0.35)" />
    </g>
  );
}

/* ---------------- Candle ---------------- */
function Candle({ x, y, s, colorIndex, snuffed, phase }) {
  const color = CANDLE_COLORS[colorIndex];
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <ellipse cx="0" cy="1" rx="12" ry="3.2" fill="rgba(0,0,0,0.3)" />
      {/* body */}
      <rect x="-9" y="-64" width="18" height="64" rx="3" fill={`url(#bc-candle-${colorIndex})`} />
      {[-52, -38, -24, -10].map((yy, i) => (
        <path key={i} d={`M-9 ${yy} l18 -8`} stroke="rgba(255,255,255,0.5)" strokeWidth="3.2" />
      ))}
      <rect x="-9" y="-64" width="18" height="64" rx="3" fill="none" stroke="rgba(0,0,0,0.12)" />
      {/* wax drip */}
      <path d="M-9 -60 q-2 9 -4 13 q-2 5 1 7 q4 -1 3 -8 q1 -6 0 -12 z" fill={mix(color, 0.5)} />
      <ellipse cx="0" cy="-64" rx="9" ry="3" fill={mix(color, 0.62)} />
      {/* wick */}
      <line x1="0" y1="-64" x2="0" y2="-73" stroke={snuffed ? "#1b1614" : "#3b2a22"} strokeWidth="2.2" strokeLinecap="round" />

      <AnimatePresence initial={false}>
        {!snuffed ? (
          <motion.g
            key="flame"
            exit={{ opacity: 0, scale: 0.2 }}
            transition={{ duration: 0.22 }}
            style={{ transformBox: "fill-box", transformOrigin: "50% 100%" }}
          >
            {/* wind bend: driven by the --wind CSS variable set on the stage */}
            <g
              style={{
                transformBox: "fill-box",
                transformOrigin: "50% 100%",
                transform:
                  "rotate(calc(var(--wind, 0) * -40deg)) scale(calc(1 - var(--wind, 0) * 0.35), calc(1 - var(--wind, 0) * 0.3))",
                transition: "transform 110ms ease-out",
              }}
            >
              <motion.g
                animate={{ scaleY: [1, 1.1, 0.94, 1.07, 1], rotate: [-2, 2.5, -1.5, 1, -2] }}
                transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut", delay: phase }}
                style={{ transformBox: "fill-box", transformOrigin: "50% 100%" }}
              >
                <ellipse cx="0" cy="-86" rx="16" ry="20" fill="url(#bc-flame-glow)" />
                <path d="M0 -104 C -8 -93, -9 -84, -6.5 -77 C -4.5 -72, 4.5 -72, 6.5 -77 C 9 -84, 8 -93, 0 -104 Z" fill="url(#bc-flame)" />
                <path d="M0 -96 C -4.5 -89, -5.5 -83, -3.8 -78 C -2.4 -74.5, 2.4 -74.5, 3.8 -78 C 5.5 -83, 4.5 -89, 0 -96 Z" fill="#ffd66b" />
                <ellipse cx="0" cy="-80" rx="1.9" ry="4" fill="#fff8dc" />
                <ellipse cx="0" cy="-74.5" rx="3" ry="1.8" fill="#6cb8ff" opacity="0.85" />
              </motion.g>
            </g>
          </motion.g>
        ) : (
          <motion.g key="smoke" initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={{ duration: 2.4, ease: "easeIn" }}>
            <motion.circle cx="0" cy="-73" r="2.2" fill="#ff9a3c" initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={{ duration: 1.4 }} />
            {[0, 1, 2, 3].map((i) => (
              <motion.circle
                key={i}
                cx="0"
                cy="-76"
                r={3 + i * 1.2}
                fill="rgba(226,226,240,0.6)"
                initial={{ y: 0, x: 0, scale: 0.5, opacity: 0.75 }}
                animate={{ y: -58 - i * 14, x: (i % 2 ? 9 : -7) + i * 2.5, scale: 2.4, opacity: 0 }}
                transition={{ duration: 1.7 + i * 0.25, ease: "easeOut", delay: i * 0.09 }}
              />
            ))}
          </motion.g>
        )}
      </AnimatePresence>
    </g>
  );
}

/* ================================================================== */
/* Chrome: embers, calibration, timer, confetti, mode picker            */
/* ================================================================== */
function AmberEmbers() {
  const embers = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 2 + Math.random() * 3,
        delay: Math.random() * 6,
        duration: 7 + Math.random() * 5,
        hue: 30 + Math.random() * 15,
      })),
    []
  );
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-[32px]">
      {embers.map((e) => (
        <motion.span
          key={e.id}
          className="absolute bottom-0 rounded-full"
          style={{
            left: `${e.left}%`,
            width: e.size,
            height: e.size,
            background: `hsl(${e.hue} 95% 65%)`,
            boxShadow: `0 0 ${e.size * 2}px hsl(${e.hue} 95% 65% / 0.7)`,
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: -520, opacity: [0, 0.9, 0.6, 0] }}
          transition={{ duration: e.duration, delay: e.delay, repeat: Infinity, ease: "easeOut" }}
        />
      ))}
    </div>
  );
}

function CalibrationOverlay({ progress, level, themeColor }) {
  const { t } = useTranslation();
  const pct = Math.round(progress * 100);
  return (
    <div
      className="relative z-10 mt-2 rounded-2xl border border-white/10 bg-black/25 p-4 text-center"
      data-testid="blow-candles-calibration"
    >
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">
        {t("blow.calibration_eyebrow")}
      </div>
      <div className="text-sm font-bold text-white">{t("blow.calibration_title")}</div>
      <div className="mt-1 text-[11.5px] text-white/60">{t("blow.calibration_sub")}</div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${themeColor}, #fbbf24, #f472b6)`,
            transition: "width 80ms linear",
          }}
          data-testid="blow-candles-calibration-progress"
        />
      </div>
      <div className="mt-2 font-mono text-[10px] text-white/35">{(level * 1000).toFixed(1)}</div>
    </div>
  );
}

function TimerRing({ value, max, themeColor }) {
  const pct = Math.max(0, Math.min(1, value / max));
  const circ = 2 * Math.PI * 14;
  const urgent = value <= 5;
  return (
    <div className="relative h-10 w-10" data-testid="blow-candles-timer">
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <circle
          cx="18"
          cy="18"
          r="14"
          fill="none"
          stroke={urgent ? "#fb7185" : "url(#bc-ring)"}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${circ * pct} ${circ}`}
          style={{ transition: "stroke-dasharray 100ms linear" }}
        />
        <defs>
          <linearGradient id="bc-ring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={themeColor} />
            <stop offset="1" stopColor="#fbbf24" />
          </linearGradient>
        </defs>
      </svg>
      <div className={`absolute inset-0 flex items-center justify-center font-mono text-[11px] font-bold ${urgent ? "text-rose-200" : "text-white/85"}`}>
        {value.toFixed(0)}
      </div>
    </div>
  );
}

function ConfettiBurst({ themeColor }) {
  const pieces = useMemo(
    () =>
      Array.from({ length: 40 }).map((_, i) => ({
        id: i,
        angle: (i / 40) * Math.PI * 2 + Math.random() * 0.3,
        dist: 120 + Math.random() * 190,
        rot: Math.random() * 360,
        color: ["#fbbf24", "#f472b6", themeColor, "#34d399", "#22d3ee", "#fef3c7"][i % 6],
        w: 4 + Math.random() * 5,
        h: 10 + Math.random() * 8,
      })),
    [themeColor]
  );
  return (
    <>
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          className="pointer-events-none absolute left-1/2 top-1/2 rounded-sm"
          style={{ width: p.w, height: p.h, background: p.color, boxShadow: `0 0 6px ${p.color}55` }}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
          animate={{ x: Math.cos(p.angle) * p.dist, y: Math.sin(p.angle) * p.dist, opacity: 0, rotate: p.rot }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
        />
      ))}
    </>
  );
}

function ModePicker({ onMic, onTap, micError, themeColor }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-[32px] border border-white/10 p-6 text-center backdrop-blur-2xl sm:p-8"
      style={{
        background: `linear-gradient(180deg, ${alpha(themeColor, 0.3)} 0%, rgba(8,6,16,0.9) 100%)`,
        boxShadow: `0 40px 100px -40px ${alpha(themeColor, 0.55)}, inset 0 1px 0 rgba(255,255,255,0.08)`,
      }}
      data-testid="blow-candles-intro"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-16 mx-auto h-48 w-48 rounded-full blur-3xl"
        style={{
          background: `conic-gradient(from 180deg at 50% 50%, ${themeColor}, #fbbf24, #f472b6, ${themeColor})`,
          opacity: 0.4,
        }}
      />
      {/* mini cake */}
      <div className="relative mx-auto mb-4 h-24 w-40">
        <svg viewBox="0 0 160 96" className="h-full w-full" aria-hidden>
          <ellipse cx="80" cy="88" rx="70" ry="7" fill="rgba(0,0,0,0.4)" />
          <ellipse cx="80" cy="84" rx="66" ry="8" fill="#efe8dc" />
          <path d="M34 50 v30 a46 9 0 0 0 92 0 v-30 z" fill={themeColor} />
          <path d="M38 56 q6 12 12 0 q6 14 12 0 q6 12 12 0 q6 14 12 0 q6 12 12 0 q6 14 12 0 q6 12 12 0" stroke={mix(themeColor, 0.78)} strokeWidth="6" fill="none" strokeLinecap="round" />
          <ellipse cx="80" cy="50" rx="46" ry="9" fill={mix(themeColor, 0.85)} />
          {[56, 80, 104].map((x, i) => (
            <g key={i}>
              <rect x={x - 4} y="24" width="8" height="26" rx="2" fill={CANDLE_COLORS[i]} />
              <path d={`M${x} 10 C ${x - 4} 15, ${x - 4} 20, ${x} 23 C ${x + 4} 20, ${x + 4} 15, ${x} 10 Z`} fill="#ffb340" />
              <ellipse cx={x} cy="18" rx="1.2" ry="2.4" fill="#fff8dc" />
            </g>
          ))}
        </svg>
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.4em] text-white/50">{t("blow.intro_eyebrow")}</div>
      <h3 className="mt-1 text-2xl font-extrabold leading-tight text-white sm:text-[28px]">{t("blow.intro_title")}</h3>
      <p className="mx-auto mt-3 max-w-sm text-sm text-white/70">{t("blow.intro_sub")}</p>

      <div className="mt-7 grid gap-3">
        <button
          type="button"
          onClick={onMic}
          className="group relative flex items-center justify-center gap-2 overflow-hidden rounded-2xl px-5 py-3.5 text-sm font-bold text-white shadow-lg transition hover:brightness-110 active:scale-[0.98]"
          style={{ background: `linear-gradient(135deg, ${themeColor} 0%, #ec4899 55%, #f59e0b 100%)` }}
          data-testid="blow-candles-mode-mic"
        >
          <Mic className="h-4 w-4" />
          <span>{t("blow.mode_mic")}</span>
          <span className="ml-auto rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
            {t("blow.badge_fun")}
          </span>
        </button>
        <button
          type="button"
          onClick={onTap}
          className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-white/10 active:scale-[0.98]"
          data-testid="blow-candles-mode-tap"
        >
          <Hand className="h-4 w-4" />
          <span>{t("blow.mode_tap")}</span>
          <span className="ml-auto rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/70">
            {t("blow.badge_silent")}
          </span>
        </button>
      </div>

      {micError && <div className="mt-4 text-xs text-rose-300" data-testid="blow-candles-mic-error">{micError}</div>}
    </motion.div>
  );
}
